import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { FormFieldDisabledGroup } from '@components/core-ui/form-elements/FormFieldDisabledGroup';
import { Input } from '@components/core-ui/form-elements/Input';
import { TProps } from './types/TProps';

const parseFiniteNumber = (value: string) => {
  if (value.trim() === '') {
    return null;
  }
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

export const NumericInput = ({
  value,
  onChange,
  label,
  name = 'numericInput',
  min,
  max,
  step = 1,
  required = false,
  disabled = false,
  clampToRange = false,
  className,
  placeholder,
  'data-testid': dataTestId,
}: TProps) => {
  const getClampedValue = (nextValue: number) => {
    let normalizedValue = nextValue;

    if (typeof min === 'number') {
      normalizedValue = Math.max(min, normalizedValue);
    }

    if (typeof max === 'number') {
      normalizedValue = Math.min(max, normalizedValue);
    }

    return normalizedValue;
  };

  const normalizeValue = (nextValue: number) => {
    if (!clampToRange) {
      return nextValue;
    }

    return getClampedValue(nextValue);
  };

  const increment = () => {
    onChange(normalizeValue(value + step));
  };

  const decrement = () => {
    onChange(normalizeValue(value - step));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const parsedValue = parseFiniteNumber(event.target.value);
    if (parsedValue === null) {
      return;
    }
    onChange(normalizeValue(parsedValue));
  };

  const isIncrementDisabled = disabled || (typeof max === 'number' && value >= max);
  const isDecrementDisabled = disabled || (typeof min === 'number' && value <= min);
  const validationError =
    typeof min === 'number' && value < min
      ? `Minimum value is ${min}`
      : typeof max === 'number' && value > max
        ? `Maximum value is ${max}`
        : '';

  return (
    <FormFieldDisabledGroup disabled={disabled} className={['w-full', className].filter(Boolean).join(' ')}>
      <div className="flex w-full items-center gap-[10px]">
        <div className="flex w-[calc(100%-90px)]">
          <Input
            inputMode="numeric"
            required={required}
            value={value}
            type="number"
            min={min}
            max={max}
            step={step}
            label={label}
            name={name}
            placeholder={placeholder}
            disabled={disabled}
            error={Boolean(validationError)}
            onChange={handleInputChange}
            data-testid={dataTestId}
          />
        </div>
        <div className="flex h-10 overflow-hidden rounded-[5px] items-center border border-[var(--color-border)] shrink-0">
          <button
            aria-label="Increment"
            type="button"
            className="flex shrink-0 w-10 text-center h-full items-center justify-center bg-[var(--color-page)] text-[var(--color-text)] transition-colors enabled:hover:bg-[var(--color-surface-muted)] enabled:hover:text-[var(--color-link)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-0 disabled:bg-[var(--color-form-field-disabled-bg)] disabled:text-[var(--color-form-field-disabled-text)]"
            onClick={increment}
            disabled={isIncrementDisabled}
          >
            +
          </button>
          <button
            aria-label="Decrement"
            type="button"
            className="flex shrink-0 w-10 text-center h-full items-center justify-center border-l border-[var(--color-border)] bg-[var(--color-page)] text-[var(--color-text)] transition-colors enabled:hover:bg-[var(--color-surface-muted)] enabled:hover:text-[var(--color-link)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-0 disabled:bg-[var(--color-form-field-disabled-bg)] disabled:text-[var(--color-form-field-disabled-text)]"
            onClick={decrement}
            disabled={isDecrementDisabled}
          >
            -
          </button>
        </div>
      </div>
      {validationError ? <ErrorMessage className="!text-left" error={validationError} /> : null}
    </FormFieldDisabledGroup>
  );
};
